<script setup>
import { onMounted, onUnmounted, reactive, ref, watch, defineProps, inject, onBeforeUnmount } from 'vue';
import { agentStream } from '../editor/aiClient';
import { marked } from 'marked';
import LoadPanel from '../LoadPanel.vue';
import copyToClipboard from 'copy-to-clipboard';
import { call } from '@/clients/internalClient.js';
import {v7 as uuidv7} from 'uuid'
import ThreadHistory from './ThreadHistory.vue';
import PromptInput from './PromptInput.vue';
import { newDocument, newThread } from '../actions/documentActions';
import { Events } from '../events';
import ToolbarBtn from '../editor/ToolbarBtn.vue';
import Tippy from '../components/Tippy.vue';
import Icon from '../Icon.vue';
import Button from '../components/Button.vue';
import { BASE_PATH } from '../env';
import { linkTo } from '../navigation/navigation';
import { getEditorHTMLContent } from '../editor/editorUtils';
import TaskIndicator from './TaskIndicator.vue';
import { capturePosthog } from '../posthog';
import BillingCredit from '@/payments/BillingCredit.vue';
import Pulsar from '../components/Pulsar.vue';
import ScrollIntoView from './ScrollIntoView.vue';
import PlaceholdersAttatchments from './messages/PlaceholdersAttatchments.vue';
import GenericAttachment from './messages/GenericAttachment.vue';

const {saveDocument} = inject('appActions')
const blockToFix = inject('blockToFix')
const billingIssue = inject('billingIssue')

const { 
  activeDocument, simpleMode, placeholder,
  agent, hideMessages, animatedPlaceholder
} = defineProps({
  activeDocument: Object,
  simpleMode: Boolean,
  placeholder: String,
  agent: String,
  hideMessages: Boolean,
  animatedPlaceholder: Boolean
})

const emit = defineEmits(['send', 'agent', 'done'])

function copyToClipboardHTML(text) {
  copyToClipboard(text, {
    format: "text/html"
  })

}

function applyBlock(html) {

  Events.dispatch(Events.REPLACE_BLOCK, { 
    content: html,
    block: blockToFix.value[0]
  })

}

function insertContent(html) {

  Events.dispatch(Events.INSERT_CONTENT, { 
    content: html
  })
}

const error = ref(false)
const closed = ref(false)
const node = ref(null);
const messages = ref([]);
const task = ref(null);
const primingMessages = ref(undefined);

const state = reactive({
  ready: simpleMode,
  streaming: false,
  currentMemoryTitle: null,
  showHistory: false
});

let autoscrollable = true;
let programmaticScroll = false;

function scrollDown() {
  const pre = node.value;
  if (pre && autoscrollable) {
    setTimeout(() => {
      programmaticScroll = true;
      pre.scrollTop = pre.scrollHeight;
    })
  }
}

watch(messages, scrollDown, { deep: true });

watch(node, scrollDown);

function getMemoryThreadId() {
  return activeDocument.activeThreadId || activeDocument.id
}

function onScroll(e) {
  if (!programmaticScroll) {
    const pre = node.value;
    if (pre) {
      autoscrollable = Math.abs(pre.scrollHeight - pre.clientHeight - pre.scrollTop) <= 1
    }
  }
  programmaticScroll = false;
}

let controller;


function send(text) {
  try {
    sendUncaught(text)
  } catch (e) {
    console.error(e)
  }
}
function sendUncaught(text) {
  emit('send', text)

  prompt.value = "";
  messages.value = [
    ...messages.value,
    {
      kind: 'user',
      text
    }
  ];
  autoscrollable = true;
  streamResponse(text);
}

let liveMarkdownUpdater = null


function splitAndMarkdownLastChunk(msg) {

  let lastChunk = msg.chunks[msg.chunks.length - 1]

  const toMarkdownProccess = []
  let keepGoing = true;
  while (keepGoing) {
    const lastChunkText = lastChunk.text;
    const openTag = lastChunkText.indexOf('<doc_block>');
    if (openTag != -1) {
      lastChunk.text = lastChunkText.substring(0, openTag);
      toMarkdownProccess.push(lastChunk)
      lastChunk = { text: lastChunkText.substring(openTag + '<doc_block>'.length), type: 'block' };
      msg.chunks.push(lastChunk)
      keepGoing = true;
    } else {
      const closeTag = lastChunkText.indexOf('</doc_block>');
      if (closeTag != -1) {
        lastChunk.text = lastChunkText.substring(0, closeTag);
        toMarkdownProccess.push(lastChunk)
        lastChunk = { text: lastChunkText.substring(closeTag + '</doc_block>'.length), type: 'normal' };
        msg.chunks.push(lastChunk)
        keepGoing = true;
      } else {
        keepGoing = false;
      }
    }
  }

  toMarkdownProccess.push(lastChunk)
  toMarkdownProccess.forEach(chunk => {
    chunk.markdown = marked(chunk.text)
  })
}

function getAttachments() {
  
  if (!simpleMode) {

    const attch = [];

    const docContent = getEditorHTMLContent()

    if (docContent) {
      attch.push({
        name: 'document',
        type: 'document',
        content: docContent
      })
    }

    if (messages.value.length <= 1 && blockToFix?.value.length) {
      blockToFix.value.forEach((b, i) => attch.push({
        name: 'block-' + (i + 1),
        type: 'block-to-fix',
        content: b.text
      }));      
    }

    console.log("sending documents:", attch)

    return attch;

  }
  return undefined;
}

function streamResponse(prompt) {

  if (liveMarkdownUpdater != null) {
    clearInterval(liveMarkdownUpdater)
  }
  liveMarkdownUpdater = setInterval(() => {
    const marr = messages.value
    const lastMsg = marr[marr.length - 1];
    if (lastMsg?.kind === 'ai') {
      const lastChunk = lastMsg.chunks[lastMsg.chunks.length - 1];
      if (lastChunk) {
        lastChunk.markdown = marked(lastChunk.text)
      }
    }
  }, 500)

  state.streaming = true
  state.updatingTitle = false
  controller = new AbortController();

  let hadError = false

  agentStream({
    prompt, 
    primingMessages: primingMessages.value,
    memoryGroupKey: activeDocument.id,
    attachments: getAttachments(),
    agent,
    threadId: getMemoryThreadId(),
    signal: controller.signal,
    onEvent: event => {

      function getWorkingMessage() {
        const marr = messages.value
        if (marr.length === 0 || marr[marr.length - 1].kind !== 'ai') {
          marr.push({
            kind: 'ai',
            text: '',
            chunks: [{ text: '', type: 'normal' }]
          })
        }
        return marr[marr.length - 1];
      }

      let stopPropagation = false;
      emit('agent', event, () => stopPropagation = true)
      if (stopPropagation) {
        return
      }

      if (event.type === 'billing-issue') {
        billingIssue.value = event.code
      } 
      
      if (event.type === 'memory-title-update') {
        state.updatingTitle = true
        state.currentMemoryTitle = ''
        state.streaming = false // release UI
        hadError = false
        return
      }

      if (event.type === 'error') {
        hadError = true
        return
      }

      if (event.type === 'tool-call-request') {
        if (event.name === 'ReplaceDocument') {
          task.value = {
            description: 'updating the document...'
          }
        }        
        return
      }

      if (event.type === 'run-end') {
        task.value = null;
        if (event.output.attachments) {
          const docReplacement = event.output.attachments.find(a => a.name === 'doc-replacement');
          if (docReplacement) {
            Events.dispatch(Events.UPDATE_DOCUMENT, docReplacement)            
          }
          getWorkingMessage().attachments = event.output.attachments.map(a => {
            a = {...a}
            delete a.content
            return a
          })
        }
        return
      }


      if (event.type !== 'token') {
        return
      }

      const token = event.token

      if (state.updatingTitle && !hadError) {
        state.currentMemoryTitle += token
        return
      }

      const last = getWorkingMessage();
      last.text += token;
      if (last.markdown) {
        last.markdown += token;
      }

      let lastChunk = last.chunks[last.chunks.length - 1]

      lastChunk.text += token

      splitAndMarkdownLastChunk(last);

    },
    onDone: () => {
      primingMessages.value = undefined

      state.streaming = false
      state.updatingTitle = false

      clearInterval(liveMarkdownUpdater)
      const marr = messages.value
      const lastMsg = marr[marr.length - 1];
      if (lastMsg && lastMsg.kind === 'ai') {
        lastMsg.chunks?.forEach(chunk => {
          if (chunk.markdown === undefined) {
            chunk.markdown = marked(chunk.text)
          }
        })
      }      
      emit('done')

      // if (simpleMode && lastMsg.chunks) {
      //   let blocks = lastMsg.chunks.filter(c => c.type === 'block' && c.markdown)
      //   const lastBlock = blocks[blocks.length - 1]
      //   if (lastBlock) {
      //     window.__DRAFT = lastBlock.markdown
      //     setTimeout(() => {
      //       window.location = linkTo('#new-document')
      //     })
      //   }        
      // }
    },
    onError: (e) => { 

      if (e.statusCode === 402) {
        billingIssue.value = e.details.code
      }

      messages.value = [
        ...messages.value,
        {
          kind: 'ai',
          chunks: [
            {
              text: e.statusCode === 402 ? e.details.text : 'Hmm... I’m having trouble connecting to my brain right now. Looks like the AI servers are unavailable. Please try again in a bit — I’ll be ready when things are back online!”'
            }            
          ]          
        }
      ];
    }
  })

  capturePosthog('chat_messages_sent', {
    block_context_num: blockToFix?.length,
    agent 
  })
}

function abort(event) {
  controller.abort()
}

function startNewThread() {
  newThread(activeDocument) 
  saveDocument()
  capturePosthog('chat_new_thread')
}

function showHistory() {
  state.showHistory = true
}

onUnmounted(() => {
});

watch(getMemoryThreadId, reloadThread, { immediate: true });

async function reloadThread() {
  try {
    if (simpleMode) {
      state.currentMemoryTitle = null
      messages.value = []
      return;
    }
    state.ready = false
    try {
      const memory = await call('GET', '/api/ai/memory/' + getMemoryThreadId())
      if (memory.messages) {

        memory.messages.forEach(m => {
          if (m.kind === 'ai') {
            m.chunks = [{ text: m.text, type: 'normal' }]
            splitAndMarkdownLastChunk(m);
          }
        })

        messages.value = memory.messages
        state.currentMemoryTitle = memory.title
      }
    } catch (error) {
      console.log(error)
    } finally {
      state.ready = true;
    }


  } catch (e) {
    console.error(e);
    error.value = true;
  }

}

onMounted(async () => {

})

Events.listen(Events.PRIME_CONTEXT, e => {
  primingMessages.value = e.detail
})

Events.listen(Events.CHAT_QUESTION, e => {
  if (!state.streaming) {
    send(e.detail.prompt)  
  }
})

const listenerGroup = Events.group();

onBeforeUnmount(() => {
  listenerGroup.dispose()
});

const showBlockToFixInEditor = () => Events.dispatch(Events.SHOW_MARK_TO_FIX)

function AIAttachmentsRenderer(attachment) {
  if (attachment.name === 'placeholders') {
    return PlaceholdersAttatchments
  }
  return GenericAttachment;
}

</script>

<template>
  <ThreadHistory v-if="state.showHistory" @close="state.showHistory=false"/>
  <!-- <div v-if="!ready && !error" class="loader">loading...</div> -->
  <div class="chat-toolbar" v-if="!simpleMode">
    <ToolbarBtn title="new chat with AI" @action="startNewThread"  class="light"><v-icon name="hi-pencil-alt"/></ToolbarBtn>
    
    <div class="a-flex-1" style="white-space: nowrap; overflow: hidden;">
      {{ state.currentMemoryTitle || '<New Chat>' }}
    </div>
    <ToolbarBtn title="chat history" @action="showHistory" ><v-icon name="ri-chat-history-line" /></ToolbarBtn>
  </div>
  <div ref="node" class="messages" :style="{display: hideMessages?'none':'flex'}" @scroll="onScroll">
    <LoadPanel v-if="!state.ready" />
    <div v-for="m in messages" :class="{ message: true, messageUser: m.kind === 'user', messageAI: m.kind !== 'user' }">
      <div v-if="m.kind === 'user'" class="text-sm">
        <span v-html="m.text"></span>
      </div>

      <div v-else v-for="chunk in m.chunks" :class="'chunk chunk-' + chunk.type">

        <div v-if="chunk.type === 'block'" class="block-controls">
          <Button title="copy content to clipboard" placement="top" class="btn-xs btn-ghost" @action="e => copyToClipboardHTML(chunk.markdown).then()">
            <Icon name="lucide--clipboard-copy" /> copy
          </Button>
          <Button v-if="!simpleMode" title="insert content at the current cursor" placement="top" class="btn-xs btn-ghost" @action="e => insertContent(chunk.markdown)">
            <Icon name="bi--input-cursor-text" /> insert
          </Button>
          <Button v-if="!simpleMode && blockToFix" title="replace marked block with this content" placement="top" class="btn-xs btn-ghost" @action="e => applyBlock(chunk.markdown)">
            <Icon name="ri--arrow-right-fill" /> apply
          </Button>
        </div>
        
        <article class="prose prose-sm" v-if="chunk.markdown" v-html="chunk.markdown"></article>
        <article class="prose prose-sm" v-else v-html="chunk.text"></article>
      </div>
      <template v-for="attachment in m.attachments" v-if="m.attachments">
        <component :is="AIAttachmentsRenderer(attachment)" :attachment="attachment" />
      </template>

    </div>
    <TaskIndicator class="self-center" :text="task.description" v-if="task !== null" />        
    <div class="self-center">
      <Pulsar v-if="!task && state.streaming"/>
      <div v-else style="height: 25px; visibility: hidden;">.</div>
    </div>

    <div v-if="closed">connection closed</div>
  </div>
  <div v-if="error" class="server-error">server is unavailable.</div>  
  <Tippy v-if="blockToFix.length > 0" class='flex items-center justify-between cursor-pointer mx-[10px] px-2 py-1 text-xs rounded bg-primary text-primary-content	' 
    @click="showBlockToFixInEditor"
    content="AI chat is in the editing mode of a selected block of text. Click to see the block in the editor." placement="top" >    
    editing selection{{ blockToFix.length > 1 ? `s(${blockToFix.length})` : '' }}
    <span class="link link-hover hover:text-error" @click="() => Events.dispatch(Events.DROP_BLOCK)">
      <Icon name="fa--close" :size="10" />
    </span>    
  </Tippy>  

  <div style="padding: 0 10px 10px 10px">    
    <PromptInput 
      :send='send'
      :abort='abort'
      :ready='state.ready'
      :streaming='state.streaming'
      :placeholder="placeholder"
      :enableAnimatedPlaceholder="animatedPlaceholder"
    >
      <template v-if="!simpleMode" #controls-right>
        <BillingCredit />
      </template>
    </PromptInput>
  </div>
</template>

<style lang='less' scoped>

.loader {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.server-error {
  .loader
}

.messages {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  padding: 0 10px;
  line-height: 1.7;
  position: relative;
}

.message {
  border-radius: 10px;
  margin: 3px 0;
  flex-grow: 0;
}

.messageAI {
  align-self: stretch;

  .chunk {
    margin: 0.8rem 0;
  }

  .chunk-block {

    border: 1px solid oklch(44.2% 0.017 285.786);
    border-radius: 10px;
    
    // background: oklch(27.8% 0.033 256.848); // var(--color-base-100);
    background: oklch(27.4% 0.006 286.033);
    position: relative;
    .block-controls {
      display: flex;
      justify-content: flex-end;
      top: 0;
      left: 10px;
      font-size: 12px;
      display: flex;
      border-bottom: 1px solid #374151;


      button {
        &:last-child {
          border-top-right-radius: 10px;
        }

        border: none;
        border-radius: 0;
        svg {
          width: 15px;
          height: 15px;
        }
      }
    }
    article {
      padding: 0.5rem 1rem;
    }
  }
}

.messageUser {
  padding: 10px 15px;
  // background: #374151;
  background: oklch(29.9% 0 0);
  .wizard-holder & {  
    background: var(--color-base-300);
  }
  // color: var(--color-primary-content);

  align-self: flex-end;
  white-space: pre-wrap;
  max-width: 490px;
}



.chat-toolbar {
  gap: 5px;
  padding: 3px;
  background: var(--color-base-200);


  display: flex;
  align-items: center;
}
</style>
